import { useTranslation } from 'react-i18next';
import { useBasePath } from '~/common/hooks/useBasePath';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from '@conform-to/react';
import { CustomSelectItem, Select, SelectContent, SelectTrigger, SelectValue } from '~/common/components/ui/select';
import { cn } from '~/common/utils/styleUtil';
import { Button } from '~/common/components/ui/button';
import { FORM_FIELDS } from '~/common/constants/formfields';
import { DialogClose, DialogFooter } from '~/common/components/ui/dialog';
import type { IInventoryLookupDtoData } from '~/dtos/ProdegaApiWebshop-api-typings-generated';
import ProgressButton from '~/common/components/ProgressButton';
import { toast } from 'sonner';

export function InventorySelect({
  onClose,
  isMobile,
  articles,
  inventories,
  isLoading,
  preselectedInventoryId
}: Readonly<{
  onClose: () => void;
  isMobile: boolean;
  articles: { mainArticleId: number; isExternalArticle: boolean; quantity: number }[];
  isLoading: boolean;
  inventories: IInventoryLookupDtoData[];
  preselectedInventoryId?: number;
}>) {
  const { t } = useTranslation();
  const { path } = useBasePath();
  const [selectedInventoryId, setSelectedInventoryId] = useState<number | null>(null);
  const [inProgress, setInProgress] = useState<boolean>(false);
  const [isOpen, setIsOpen] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [form, fields] = useForm({
    shouldValidate: 'onBlur',
    shouldRevalidate: 'onInput'
  });

  useEffect(() => {
    if (!selectedInventoryId && inventories?.length > 0) {
      if (preselectedInventoryId) {
        if (
          inventories.some(template => template.id === preselectedInventoryId && !template.isArticleAlreadyInTemplate)
        ) {
          // Set the preselected template as the selected inventory if it is available and not already selected
          // eslint-disable-next-line react-hooks/set-state-in-effect
          setSelectedInventoryId(preselectedInventoryId);
        }
      } else {
        const firstAvailableInventory = inventories.find(inv => !inv.isArticleAlreadyInTemplate);
        if (firstAvailableInventory) {
          // Set the first available template as the selected template if no preselected template is available and no template is already selected
          setSelectedInventoryId(firstAvailableInventory.id);
        }
      }
    }
  }, [selectedInventoryId, inventories, preselectedInventoryId]);

  const handleSubmit = useCallback(
    async event => {
      event.preventDefault();

      try {
        setInProgress(true);
        const formData = new FormData(event.target);
        formData.append(FORM_FIELDS.Intent, FORM_FIELDS.Select);

        const response = await fetch(`${path}/inventory/addarticletoinventory`, {
          method: 'POST',
          body: formData
        });

        if (response?.ok) {
          const data = (await response.json()) as { ok: boolean; message: string };
          if (data.message) {
            if (data.ok) {
              toast.success(data.message);
              onClose();
            } else {
              toast.error(data.message);
            }
          }
        }
      } finally {
        setInProgress(false);
      }
    },
    [onClose, path]
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Element;

      // Check if the click is inside our form component
      if (formRef.current?.contains(target)) {
        return;
      }

      // Check if the click is inside the Select portal element
      const isInSelectPortal =
        target.closest('[data-radix-popper-content-wrapper]') ||
        target.closest('[data-radix-select-content]') ||
        target.closest('[data-radix-select-item]') ||
        target.closest('[data-radix-select-trigger]');

      // Only close if click is outside both our component and the select portal
      if (!isInSelectPortal && isOpen) {
        // Delay closing the select to allow to work properly when clicking on the select trigger with touch devices
        setTimeout(() => setIsOpen(false), 300);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isOpen]);

  const selectedInventory = useMemo(
    () => inventories.find(inventory => inventory.id === selectedInventoryId),
    [inventories, selectedInventoryId]
  );
  const isAlreadySelected = useMemo(
    () => selectedInventoryId && selectedInventory?.isArticleAlreadyInTemplate,
    [selectedInventoryId, selectedInventory]
  );

  const disableAddButton = useMemo(
    () =>
      selectedInventoryId == null ||
      isAlreadySelected ||
      (inventories?.every(inventory => inventory.isArticleAlreadyInTemplate) ?? false),
    [inventories, selectedInventoryId, isAlreadySelected]
  );

  const preselectedInventory = useMemo(
    () => inventories.find(inventory => inventory.id === preselectedInventoryId),
    [inventories, preselectedInventoryId]
  );

  const isPreselectedInventoryAlreadyApplied = useMemo(
    () => preselectedInventory?.isArticleAlreadyInTemplate,
    [preselectedInventory]
  );

  return (
    <form
      ref={formRef}
      method="post"
      className={cn('flex flex-col', isMobile ? 'h-full' : 'justify-between h-full')}
      onSubmit={handleSubmit}
    >
      <input name="articles" type="hidden" defaultValue={JSON.stringify(articles)} />
      <input name="selectedInventoryId" type="hidden" defaultValue={selectedInventoryId?.toString()} />
      <div className={'flex-1'}>
        <p className={'my-2'}>{t('In welches Inventar möchten Sie den Artikel hinzufügen?')}</p>
        <Select
          open={isOpen}
          onOpenChange={setIsOpen}
          disabled={isLoading}
          value={selectedInventoryId?.toString()}
          onValueChange={value => {
            setSelectedInventoryId(Number.parseInt(value, 10));
          }}
        >
          <SelectTrigger
            className={cn('w-full', !isLoading && !selectedInventoryId && 'border-primary focus:ring-primary')}
            data-testid={'inventory-select'}
          >
            {!selectedInventoryId && isPreselectedInventoryAlreadyApplied ? (
              <div className={'flex justify-between content-between w-full opacity-60 pointer-events-none'}>
                <div>{preselectedInventory?.name}</div>
                <div className={'text-primary pr-2'}>{t('in Inventur vorhanden')}</div>
              </div>
            ) : (
              <SelectValue className="pointer-events-none" />
            )}
          </SelectTrigger>
          <SelectContent side={'bottom'} avoidCollisions={false} className={'desktop:max-h-60'}>
            {inventories.map(inv => (
              <CustomSelectItem
                key={inv.id}
                value={inv.id.toString()}
                data-testid={`inventory-select-${inv.id}`}
                badge={
                  inv.isArticleAlreadyInTemplate ? (
                    <div className={'text-primary'}>{t('in Inventur vorhanden')}</div>
                  ) : null
                }
                disabled={inv.isArticleAlreadyInTemplate}
              >
                {inv.name}
              </CustomSelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="mt-1 min-h-6 text-sm text-primary">{fields.name.errors?.map(e => t(e as any))}</div>
      </div>
      <DialogFooter className={'flex-col desktop:flex-row gap-2 desktop:gap-0'}>
        <DialogClose asChild>
          <Button variant="primaryOutline" data-testid={'article-to-inventory-dialog-cancel-button'}>
            {t('Abbrechen')}
          </Button>
        </DialogClose>
        <ProgressButton
          inProgress={inProgress}
          name={FORM_FIELDS.Intent}
          value={FORM_FIELDS.Select}
          type="submit"
          data-testid={'article-to-inventory-dialog-ok-button'}
          disabled={disableAddButton}
        >
          {t('Hinzufügen')}
        </ProgressButton>
      </DialogFooter>
    </form>
  );
}
